import { doc, getDoc, collection, getDocs, query, where, orderBy, Timestamp, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { getPrimaryUserIdForAccount } from './firestoreService';

// Build the summary shape every consumer expects from a selections array.
// Counts are always computed from the selections themselves so stale summary
// fields can never disagree with the stack being displayed.
const summarizeSelections = (selectedSoftware) => {
  const selections = Array.isArray(selectedSoftware) ? selectedSoftware : [];
  const softwareCount = selections.filter(sw => sw.name && sw.name !== 'None').length;
  const integratedCount = selections.filter(sw => sw.open).length;
  return {
    annualCost: 0,
    softwareCount,
    integrationPercentage: softwareCount > 0 ? Math.round((integratedCount / softwareCount) * 100) : 0,
    selectedSoftware: selections
  };
};

// Legacy per-user tech stack lookup, used only to seed an account doc that has
// no selections yet: primary contact's user doc first, then the current user's.
const findLegacyTechStack = async (userId, accountId) => {
  const candidateIds = [];
  const primaryUserId = await getPrimaryUserIdForAccount(accountId);
  if (primaryUserId) candidateIds.push(primaryUserId);
  if (userId && !candidateIds.includes(userId)) candidateIds.push(userId);

  for (const candidateId of candidateIds) {
    const userDoc = await getDoc(doc(db, 'users', candidateId));
    if (!userDoc.exists()) continue;
    const techStack = userDoc.data().techStack;
    if (Array.isArray(techStack?.selectedSoftware) && techStack.selectedSoftware.length) {
      return techStack;
    }
  }
  return null;
};

// Get user's/company's tech stack data.
// Company users (accountId present): accounts/{accountId}.techStack is the ONLY
// source of truth. Legacy per-user data is copied into the account doc once
// (migrate-on-read) and never read as a live fallback again — reading from
// multiple docs is what made different stacks appear for the same company.
export const getUserTechStackData = async (userId, accountId = null) => {
  try {
    if (accountId) {
      const accountRef = doc(db, 'accounts', accountId);
      const accountDoc = await getDoc(accountRef);
      const accountTechStack = accountDoc.exists() ? (accountDoc.data().techStack || {}) : {};

      if (Array.isArray(accountTechStack.selectedSoftware) && accountTechStack.selectedSoftware.length) {
        return summarizeSelections(accountTechStack.selectedSoftware);
      }

      // Account has no selections yet — seed it once from legacy per-user data
      const legacy = await findLegacyTechStack(userId, accountId);
      if (legacy) {
        const migrated = {
          selectedSoftware: legacy.selectedSoftware,
          lastUpdated: new Date().toISOString()
        };
        // Only take the legacy proposed list if the account doesn't already have one
        if (
          (!Array.isArray(accountTechStack.proposedSoftware) || !accountTechStack.proposedSoftware.length) &&
          Array.isArray(legacy.proposedSoftware) && legacy.proposedSoftware.length
        ) {
          migrated.proposedSoftware = legacy.proposedSoftware;
        }
        try {
          await setDoc(accountRef, { techStack: migrated }, { merge: true });
        } catch (migrateError) {
          console.error('Error migrating legacy tech stack to account doc:', migrateError);
        }
        return summarizeSelections(legacy.selectedSoftware);
      }

      // Company user with a genuinely empty stack — never fall through to the
      // per-user doc, or two teammates would see two different stacks
      return summarizeSelections([]);
    }

    // No company: user-scoped stack (the only case where the user doc is used)
    const userDoc = await getDoc(doc(db, 'users', userId));
    return summarizeSelections(userDoc.exists() ? userDoc.data().techStack?.selectedSoftware : []);
  } catch (error) {
    console.error('Error fetching user tech stack data:', error);
    return {
      annualCost: 0,
      softwareCount: 0,
      integrationPercentage: 0,
      selectedSoftware: []
    };
  }
};

// Get user's automation data
export const getUserAutomationData = async (userId) => {
  try {
    // Get user document to check automation limits and reset info
    const userDoc = await getDoc(doc(db, 'users', userId));
    let totalRunsAllowed = 100; // Default monthly limit
    let resetDate = null;
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const automationData = userData.automation || {};
      totalRunsAllowed = automationData.monthlyLimit || 100;
      resetDate = automationData.resetDate || null;
    }
    
    // Get automation runs from automations collection
    const automationsRef = collection(db, 'automations');
    const userAutomationsQuery = query(
      automationsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const automationsSnapshot = await getDocs(userAutomationsQuery);
    let totalRuns = 0;
    
    automationsSnapshot.forEach(doc => {
      const automation = doc.data();
      totalRuns += automation.runs || 0;
    });

    // Calculate days until reset (default to monthly reset)
    let daysUntilReset = 30;
    if (resetDate) {
      const reset = new Date(resetDate);
      const now = new Date();
      const diffTime = reset.getTime() - now.getTime();
      daysUntilReset = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } else {
      // If no reset date set, calculate based on current month
      const now = new Date();
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const diffTime = lastDayOfMonth.getTime() - now.getTime();
      daysUntilReset = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    return {
      runs: totalRuns,
      totalRunsAllowed,
      activeAutomations: automationsSnapshot.size,
      daysUntilReset: Math.max(0, daysUntilReset)
    };
  } catch (error) {
    console.error('Error fetching user automation data:', error);
    return {
      runs: 0,
      totalRunsAllowed: 100,
      activeAutomations: 0,
      daysUntilReset: 30
    };
  }
};

// Get user's GPTs data
export const getUserGPTsData = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return {
        claimedPercentage: 0,
        totalGPTs: 8, // Default total available GPTs
        claimedGPTs: 0,
        customGPTs: []
      };
    }

    const userData = userDoc.data();
    const gptsData = userData.gpts || {};
    
    const totalGPTs = 8; // Default total available GPTs
    const claimedGPTs = gptsData.claimedGPTs || [];
    const customGPTs = gptsData.customGPTs || [];
    
    const claimedPercentage = totalGPTs > 0 ? Math.round((claimedGPTs.length / totalGPTs) * 100) : 0;

    return {
      claimedPercentage,
      totalGPTs,
      claimedGPTs: claimedGPTs.length,
      customGPTs: customGPTs.length,
      allGPTs: [...claimedGPTs, ...customGPTs]
    };
  } catch (error) {
    console.error('Error fetching user GPTs data:', error);
    return {
      claimedPercentage: 0,
      totalGPTs: 8,
      claimedGPTs: 0,
      customGPTs: 0,
      allGPTs: []
    };
  }
};

// Get user's calculators data
export const getUserCalculatorsData = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return {
        claimed: 0,
        totalCalculators: 12, // Default total available calculators
        usedCalculators: []
      };
    }

    const userData = userDoc.data();
    const calculatorsData = userData.calculators || {};
    
    const totalCalculators = 12; // Default total available calculators
    const usedCalculators = calculatorsData.usedCalculators || [];
    
    return {
      claimed: usedCalculators.length,
      totalCalculators,
      usedCalculators
    };
  } catch (error) {
    console.error('Error fetching user calculators data:', error);
    return {
      claimed: 0,
      totalCalculators: 12,
      usedCalculators: []
    };
  }
};

// Get user's current technology project/roadmap
export const getUserTechnologyProject = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return {
        title: 'Technology Implementation',
        progress: 0,
        description: 'Start implementing technology solutions for your business'
      };
    }

    const userData = userDoc.data();
    const techProject = userData.techProject || {};
    
    return {
      title: techProject.title || 'Technology Implementation',
      progress: techProject.progress || 0,
      description: techProject.description || 'Implementing technology solutions for your business',
      lastUpdated: techProject.lastUpdated || null
    };
  } catch (error) {
    console.error('Error fetching user technology project:', error);
    return {
      title: 'Technology Implementation',
      progress: 0,
      description: 'Start implementing technology solutions for your business'
    };
  }
};

// Get comprehensive user technology data
export const getUserTechnologyData = async (userId) => {
  try {
    const [techStackData, automationData, gptsData, calculatorsData, projectData] = await Promise.all([
      getUserTechStackData(userId),
      getUserAutomationData(userId),
      getUserGPTsData(userId),
      getUserCalculatorsData(userId),
      getUserTechnologyProject(userId)
    ]);

    return {
      techStack: techStackData,
      automation: automationData,
      gpts: gptsData,
      calculators: calculatorsData,
      currentProject: projectData
    };
  } catch (error) {
    console.error('Error fetching user technology data:', error);
    return {
      techStack: { annualCost: 0, softwareCount: 0, integrationPercentage: 0, selectedSoftware: [] },
      automation: { runs: 0, activeAutomations: 0 },
      gpts: { claimedPercentage: 0, totalGPTs: 8, claimedGPTs: 0, customGPTs: 0, allGPTs: [] },
      calculators: { claimed: 0, totalCalculators: 12, usedCalculators: [] },
      currentProject: { title: 'Technology Implementation', progress: 0, description: 'Start implementing technology solutions for your business' }
    };
  }
};

// Selections spread template software objects, whose optional fields (e.g.
// order) can be undefined — Firestore rejects undefined, so drop those keys.
const stripUndefined = (selections) =>
  (selections || []).map((item) =>
    Object.fromEntries(Object.entries(item || {}).filter(([, value]) => value !== undefined))
  );

// Update user's/company's tech stack selection
export const updateUserTechStack = async (userId, rawSelectedSoftware, accountId = null) => {
  const selectedSoftware = stripUndefined(rawSelectedSoftware);
  try {
    if (accountId) {
      // Save tech stack at the company/account level so all team members share
      // it. setDoc + merge deep-merges the techStack map, so proposedSoftware
      // and other sibling fields are preserved.
      const accountRef = doc(db, 'accounts', accountId);
      await setDoc(accountRef, {
        techStack: {
          selectedSoftware,
          lastUpdated: new Date().toISOString()
        }
      }, { merge: true });
    } else {
      // Fallback: user-scoped tech stack
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        'techStack.selectedSoftware': selectedSoftware,
        'techStack.lastUpdated': new Date().toISOString()
      });
    }
    return true;
  } catch (error) {
    console.error('Error updating user tech stack:', error);
    throw error;
  }
};

// Update user's GPTs selection
export const updateUserGPTs = async (userId, claimedGPTs, customGPTs = []) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      'gpts.claimedGPTs': claimedGPTs,
      'gpts.customGPTs': customGPTs,
      'gpts.lastUpdated': new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating user GPTs:', error);
    throw error;
  }
};

// Update user's calculators usage
export const updateUserCalculators = async (userId, usedCalculators) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      'calculators.usedCalculators': usedCalculators,
      'calculators.lastUpdated': new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating user calculators:', error);
    throw error;
  }
};

// Update user's technology project
export const updateUserTechnologyProject = async (userId, projectData) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      'techProject': {
        ...projectData,
        lastUpdated: new Date().toISOString()
      }
    });
    return true;
  } catch (error) {
    console.error('Error updating user technology project:', error);
    throw error;
  }
}; 